import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Alert,
  LinearProgress,
  useTheme,
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import {
  Close as CloseIcon,
  Edit as EditIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import type {
  MedicalReservationRecord,
  MedicalReservationUpdateRequest,
  ActionStatus,
} from '../../types/medicalReservation';
import { ACTION_STATUS_CONFIG } from '../../types/medicalReservation';

interface EditModalProps {
  open: boolean;
  record: MedicalReservationRecord | null;
  onClose: () => void;
  onSave: (id: string, data: MedicalReservationUpdateRequest) => Promise<MedicalReservationRecord>;
}

/**
 * 診療予約レコード編集モーダル
 * 
 * 意図：
 * - 担当医師とアクションステータスの更新
 * - 診療リスト登録状態の管理
 * 
 * 注意点：
 * - バリデーション処理
 * - 楽観的更新のエラーハンドリング
 * - フォーム状態の管理
 * 
 * 改善提案：
 * - リアルタイムバリデーション
 * - 自動保存機能
 * - 変更履歴の表示
 */
export const EditModal: React.FC<EditModalProps> = ({
  open,
  record,
  onClose,
  onSave,
}) => {
  const theme = useTheme();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<MedicalReservationUpdateRequest>({});

  // フォーム初期化
  useEffect(() => {
    if (record) {
      setFormData({
        actionStatus: record.actionStatus,
        csvData: {
          ...(record.csvData || {}),
          '担当医師': record.doctorInCharge,
        },
      });
    }
  }, [record]);

  const handleClose = () => {
    setFormData({});
    setError(null);
    onClose();
  };

  const handleInputChange = (field: keyof MedicalReservationUpdateRequest, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    if (!record) return;

    setIsLoading(true);
    setError(null);

    try {
      // 変更があるフィールドのみを送信
      const updateData: MedicalReservationUpdateRequest = {};
      
      if (formData.actionStatus && formData.actionStatus !== record.actionStatus) {
        updateData.actionStatus = formData.actionStatus;
      }
      
      // 担当医師が変更された場合
      const newDoctor = formData.csvData && formData.csvData['担当医師'];
      if (newDoctor && newDoctor !== record.doctorInCharge) {
        updateData.csvData = {
          ...(record.csvData || {}),
          '担当医師': newDoctor,
        };
      }

      // 変更がない場合は保存せずに閉じる
      if (Object.keys(updateData).length === 0) {
        handleClose();
        return;
      }

      await onSave(record.id, updateData);
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : '保存に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  // バリデーション
  const isValid = () => {
    const doctorName = formData.csvData && formData.csvData['担当医師'];
    if (!doctorName || !doctorName.trim()) {
      return false;
    }
    return true;
  };

  if (!record) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: `1px solid ${theme.palette.divider}`,
          pb: 2,
        }}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <EditIcon sx={{ color: theme.palette.primary.main }} />
          <Box>
            <Typography variant="h6" fontWeight={600}>
              レコード編集
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {record.patientName} ({record.anybotId})
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={handleClose} size="small" disabled={isLoading}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {isLoading && <LinearProgress sx={{ mb: 2 }} />}
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Grid container spacing={3}>
            {/* 基本情報表示 */}
            <Grid item xs={12}>
              <Box
                sx={{
                  p: 2,
                  bgcolor: theme.palette.grey[50],
                  borderRadius: 1,
                  border: `1px solid ${theme.palette.divider}`,
                }}
              >
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  基本情報
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      患者名
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {record.patientName}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      診療開始時間
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {record.consultationStartTime}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      anybot ID
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {record.anybotId}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </Grid>

            {/* 担当医師 */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="担当医師"
                value={(formData.csvData && formData.csvData['担当医師']) || ''}
                onChange={(e) => handleInputChange('csvData', {
                  ...formData.csvData,
                  '担当医師': e.target.value,
                })}
                disabled={isLoading}
                required
                error={!(formData.csvData && formData.csvData['担当医師'] && formData.csvData['担当医師'].trim())}
                helperText={!(formData.csvData && formData.csvData['担当医師'] && formData.csvData['担当医師'].trim()) ? '担当医師名を入力してください' : ''}
              />
            </Grid>

            {/* アクションステータス */}
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>アクションステータス</InputLabel>
                <Select
                  value={formData.actionStatus || ''}
                  label="アクションステータス"
                  onChange={(e) => handleInputChange('actionStatus', e.target.value as ActionStatus)}
                  disabled={isLoading}
                >
                  {Object.entries(ACTION_STATUS_CONFIG).map(([status, config]) => (
                    <MenuItem key={status} value={status}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <span>{config.icon}</span>
                        {config.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

          </Grid>
        </motion.div>
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
        <Button 
          onClick={handleClose} 
          variant="outlined" 
          disabled={isLoading}
        >
          キャンセル
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={isLoading || !isValid()}
          startIcon={isLoading ? null : <SaveIcon />}
        >
          {isLoading ? '保存中...' : '保存'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};